'use client';

import { useContext, useMemo } from 'react';

import { FundingKey, ProductModel } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import { DirectionEnum, FlexBox, FlexItem } from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';
import { PriceFundingsDetails } from '@app-types/product';
import { UserType } from '@app-types/user';

import { PlanRange } from '@constants/plan';

import { getSimulatedPhone } from '@offer-wall/helpers';
import { PricesDetails } from '@product-detail/helpers';

import { OfferCard } from './offer-card';
import { PhoneInCart } from './phone-in-cart';

type OfferCardContainerProps = {
    user?: UserType;
    plan: MobilePlanType & { priceDetails?: PricesDetails };
    openOfferDetails: (offer: MobilePlanType) => void;
    phoneInCart?: { phone: ProductModel; details?: PriceFundingsDetails };
    isPhoneLoading: boolean;
    onAddToCart: (
        selectedOffer: MobilePlanType,
        fundingKey?: FundingKey,
        fromUpgradeModal?: boolean,
        redirectToSmartphonesWall?: boolean,
    ) => Promise<void>;
    isConnected?: boolean;
    isSmallPrice: boolean;
    hatInLine: boolean;
    showBlockedPlanZone: boolean;
    hasBigInnerHeight: boolean;
};

export function OfferCardContainer({
    user,
    plan,
    openOfferDetails,
    phoneInCart,
    isPhoneLoading,
    onAddToCart,
    isConnected = false,
    isSmallPrice,
    hatInLine,
    showBlockedPlanZone,
    hasBigInnerHeight,
}: Readonly<OfferCardContainerProps>) {
    const fundings = getSimulatedPhone(plan.simulatedCart, phoneInCart?.phone?.gencode)?.details.fundings;
    const { cart } = useContext(CartContext);
    const cartFunding = cart?.funding;

    const selectedFunding = useMemo(() => {
        if (!fundings) return undefined;

        const preferredFunding = fundings.find((f) => {
            return cartFunding ? f.key === cartFunding.key : false;
        });

        if (preferredFunding) return preferredFunding;

        return (
            fundings.find((f) => f.key === FundingKey.YOUNITED_X24) ??
            fundings.find((f) => f.key === FundingKey.EDP) ??
            fundings.find((f) => f.key === FundingKey.COMPTANT)
        );
    }, [cartFunding, fundings]);

    return (
        <FlexItem
            size={{
                tablet: 6,
                desktop: 6,
                widescreen: 4,
            }}
            id={plan.id}
        >
            <FlexBox direction={DirectionEnum.COLUMN} fullheight>
                {phoneInCart && plan?.details?.price && plan.commercialRange === PlanRange.WITH_MOBILE && (
                    <PhoneInCart phone={phoneInCart} plan={plan} selectedFunding={selectedFunding} />
                )}
                <OfferCard
                    user={user}
                    offer={plan}
                    openOfferDetails={openOfferDetails}
                    isPhoneLoading={isPhoneLoading}
                    onAddToCart={(selectedOffer, fundingKey) =>
                        onAddToCart(selectedOffer, fundingKey, false, !phoneInCart)
                    }
                    isConnected={isConnected}
                    isSmallPrice={isSmallPrice}
                    showBlockedPlanZone={showBlockedPlanZone}
                    hatInLine={hatInLine}
                    selectedFunding={selectedFunding}
                    hasBigInnerHeight={hasBigInnerHeight}
                />
            </FlexBox>
        </FlexItem>
    );
}
